import { Component, Mixins } from "vue-property-decorator";
import {
  AiGenerateHistory,
  IbowAiGenerateOption,
  DefaultIbowAiGenerateOption,
  AiSetChoice
} from "@/views/patient/plan/types";
import {
  AiVnplanSetting,
  DefaultAiVnplanSetting,
  AiMonthReportSetting,
  DefaultAiMonthReportSetting
} from "#/model/admin";
import AxiosMixin from "@/mixins/axiosMixin";
import FireStoreMixin, { Collections } from "@/mixins/firestoreMixin";
import UtilMixin from "@/mixins/utilMixin";
import * as CONST from "@/components/patient/common/aireport/const";
import * as appDate from "#/utility/appDate";
import { db } from "#/mixins/firebase";

/** AI書類自動作成用の共通処理 */
@Component
export default class AiReportMixin extends Mixins(
  AxiosMixin,
  FireStoreMixin,
  UtilMixin
) {
  /** 変数 */

  /** リアルタイムアップデート用リスナーをデタッチする関数 */
  protected detach = () => {
    return;
  };

  /** AI自動作成履歴 */
  protected generateHistories: AiGenerateHistory[] = [];

  /** AI自動作成書類タイプ */
  protected report_type = 0;

  /** 「iBow AI自動作成」作成オプション */
  protected ibowAiGenerateOption: IbowAiGenerateOption = DefaultIbowAiGenerateOption();

  /** 月次報告書AI自動作成設定 */
  protected aiMonthReportConfig: AiMonthReportSetting = DefaultAiMonthReportSetting();

  /** 職員ID */
  private documentId = "";

  /** 月次報告書AIセットリスト(表示用) */
  protected monthReportAiSets: AiSetChoice[] = [];

  /** 月次報告書AIセットリスト(引き当て用) */
  protected monthReportAiSetsForApply: AiSetChoice[] = [];

  /** 看護計画書AI自動作成設定 */
  protected aiVnplanConfig: AiVnplanSetting = DefaultAiVnplanSetting();

  /** 看護計画書AIセットリスト(表示用) */
  protected vnplanAiSets: AiSetChoice[] = [];

  /** 看護計画書AIセットリスト(引き当て用) */
  protected vnplanAiSetsForApply: AiSetChoice[] = [];

  /** getter */

  /** AI自動作成中フラグ */
  protected get IsAiGenerating(): boolean {
    // 1件もない場合は作成中ではない
    if (this.generateHistories.length === 0) {
      return false;
    }

    const latestHistory = this.generateHistories[0];

    // 最新が「作成中」状態かどうか
    if (latestHistory.state != CONST.AI_GENERATE_STATE_PROGRESS) {
      return false;
    }

    // 「作成中」状態だが、一定時間経過している場合は作成中ではないとみなす（ステータスの更新失敗に対する保険）
    const latest = appDate.strToDateObj(latestHistory.datetime);
    const now = appDate.strToDateObj();
    if (appDate.diffMinute(now, latest) >= 5) {
      return false;
    }

    return true;
  }

  /** 関数 */

  beforeDestroy() {
    // リアルタイムアップデート用リスナーをデタッチ
    this.detach();
  }

  /** 作成オプション情報の取得 */
  protected async fetchGenerateOption(staffId: number) {
    // 初期値作成
    this.ibowAiGenerateOption = DefaultIbowAiGenerateOption();

    this.postJsonCheck(
      window.base_url + "/api/patient/report/aigenerate-setting/get",
      {},
      async res => {
        this.aiMonthReportConfig = res.data.month_report_ai_config;
        this.monthReportAiSetsForApply = res.data.month_report_ai_sets;
        this.monthReportAiSets = this.monthReportAiSetsForApply.filter(
          aiSet => !aiSet.is_delete && aiSet.is_show
        );
        this.aiVnplanConfig = res.data.vnplan_ai_config;
        this.vnplanAiSetsForApply = res.data.vnplan_ai_sets;
        this.vnplanAiSets = this.vnplanAiSetsForApply.filter(
          aiSet => !aiSet.is_delete && aiSet.is_show
        );

        // 管理画面側のデフォルト値で上書き
        // 計画書
        const selectableVnplanAiSets = this.vnplanAiSetsForApply.filter(
          aiSet => {
            return (
              (this.aiVnplanConfig.is_use_ai_set == 1 &&
                !aiSet.is_delete &&
                aiSet.is_show &&
                aiSet.is_valid) ||
              (this.aiVnplanConfig.is_use_ai_set == 0 &&
                !aiSet.is_delete &&
                aiSet.is_valid)
            );
          }
        );
        const vnplanHeadAiSetId =
          selectableVnplanAiSets.length > 0
            ? Number(selectableVnplanAiSets[0].id)
            : 0;
        this.ibowAiGenerateOption.vnplan.ai_set_id = vnplanHeadAiSetId;

        // 報告書
        this.ibowAiGenerateOption.month_report.use_vital = this.aiMonthReportConfig.default_vital_option;
        this.ibowAiGenerateOption.month_report.use_observation = this.aiMonthReportConfig.default_observation_option;
        this.ibowAiGenerateOption.month_report.use_vn_plan = this.aiMonthReportConfig.default_vn_plan_option;
        this.ibowAiGenerateOption.month_report.use_vn_record_fam = this.aiMonthReportConfig.default_vn_record_fam_option;
        this.ibowAiGenerateOption.month_report.use_pre_month_report = this.aiMonthReportConfig.default_pre_month_report_option;
        this.ibowAiGenerateOption.month_report.generate_betten = this.aiMonthReportConfig.default_betten_option;
        this.ibowAiGenerateOption.month_report.generate_daily_life_rate_div = this.aiMonthReportConfig.default_daily_life_rate_div_option;
        this.ibowAiGenerateOption.month_report.generate_dementia_daily_life_rate_div = this.aiMonthReportConfig.default_dementia_daily_life_rate_div_option;
        this.ibowAiGenerateOption.month_report.generate_contents = this.aiMonthReportConfig.default_contents_option;
        this.ibowAiGenerateOption.month_report.generate_adl = this.aiMonthReportConfig.default_adl_option;
        this.ibowAiGenerateOption.month_report.generate_communication = this.aiMonthReportConfig.default_communication_option;
        this.ibowAiGenerateOption.month_report.generate_role_in_the_home = this.aiMonthReportConfig.default_role_in_the_home_option;
        this.ibowAiGenerateOption.month_report.generate_leisure_activities = this.aiMonthReportConfig.default_leisure_activities_option;
        this.ibowAiGenerateOption.month_report.generate_volunteer_activities = this.aiMonthReportConfig.default_volunteer_activities_option;
        this.ibowAiGenerateOption.month_report.generate_activities_after = this.aiMonthReportConfig.default_activities_after_option;

        const selectableMonthReportAiSets = this.monthReportAiSetsForApply.filter(
          aiSet => {
            return (
              (this.aiMonthReportConfig.is_use_ai_set == 1 &&
                !aiSet.is_delete &&
                aiSet.is_show &&
                aiSet.is_valid) ||
              (this.aiMonthReportConfig.is_use_ai_set == 0 &&
                !aiSet.is_delete &&
                aiSet.is_valid)
            );
          }
        );
        const monthReportHeadAiSetId =
          selectableMonthReportAiSets.length > 0
            ? Number(selectableMonthReportAiSets[0].id)
            : 0;
        this.ibowAiGenerateOption.month_report.ai_set_id = monthReportHeadAiSetId;

        // Firestoreから設定を取得できればさらに上書き
        this.documentId = String(staffId);
        const { data } = await this.getFirestore(
          Collections.IbowAiGenerateOption,
          this.documentId
        );
        if (!data) {
          return;
        }

        // 計画書
        if (data.vnplan) {
          this.ibowAiGenerateOption.vnplan = {
            ...this.ibowAiGenerateOption.vnplan,
            ...data.vnplan
          };

          // 記憶済みAIセットが現在有効なリストの中にない場合、先頭のものを利用
          if (
            selectableVnplanAiSets.findIndex(
              aiSet =>
                Number(aiSet.id) == this.ibowAiGenerateOption.vnplan.ai_set_id
            ) === -1
          ) {
            this.ibowAiGenerateOption.vnplan.ai_set_id = vnplanHeadAiSetId;
          }
        }

        if (data.month_report) {
          this.ibowAiGenerateOption.month_report = {
            ...this.ibowAiGenerateOption.month_report,
            ...data.month_report
          };

          // 記憶済みAIセットが現在有効なリストの中にない場合、先頭のものを利用
          if (
            selectableMonthReportAiSets.findIndex(
              aiSet =>
                Number(aiSet.id) ==
                this.ibowAiGenerateOption.month_report.ai_set_id
            ) === -1
          ) {
            this.ibowAiGenerateOption.month_report.ai_set_id = monthReportHeadAiSetId;
          }
        }
      }
    );
  }

  /** 作成オプション情報の保存 */
  protected saveGenerateOption() {
    this.setFirestore(
      Collections.IbowAiGenerateOption,
      this.documentId,
      this.ibowAiGenerateOption
    );
  }

  /** リアルタイムアップデート用リスナーの作成 */
  protected createAiReportListener(
    patientId: number,
    reportType: number,
    fetchFunc: (obj: { tab: number; page: number }) => void
  ) {
    this.detach = db
      .collection(Collections.AiGenerateState)
      .where("agreement_id", "==", this.loginUser.agreement_id)
      .where("patient_id", "==", patientId)
      .where("report_type", "==", reportType)
      .orderBy("datetime", "asc")
      .onSnapshot(ss => {
        ss.docChanges().forEach(docChange => {
          const index = this.generateHistories.findIndex(item => {
            return item.id === docChange.doc.id;
          });

          const stateData = docChange.doc.data() as AiGenerateHistory;
          stateData.id = docChange.doc.id;

          switch (docChange.type) {
            // 新規ドキュメント追加(=新しく自動作成)
            case "added": {
              // 履歴リストに項目追加
              this.generateHistories.unshift(stateData);
              break;
            }
            // ドキュメント更新(=自動作成完了or失敗)
            case "modified": {
              if (index === -1) {
                break;
              }

              const beforeState = this.generateHistories[index].state;
              const afterState = stateData.state;

              // 再取得して一覧画面を更新
              // ・作成中>作成完了
              // ・「未提出」タブ
              if (
                beforeState == CONST.AI_GENERATE_STATE_PROGRESS &&
                afterState == CONST.AI_GENERATE_STATE_SUCCESS
              ) {
                if (
                  !this.$route.query.reporttab ||
                  this.$route.query.reporttab === "0"
                ) {
                  fetchFunc({ tab: 0, page: 0 });
                }
              }

              // 履歴リストの項目更新
              this.generateHistories.splice(index, 1, stateData);

              break;
            }
            // ドキュメント削除(=履歴削除)
            case "removed": {
              // 履歴は削除できない
            }
          }
        });
      });
  }

  /** AI選択リスト表示名取得 */
  protected getAiSetNameFromId(type: number, id: number) {
    return type == 1
      ? this.vnplanAiSetsForApply.find(aiSet => aiSet.id == id)?.name
      : this.monthReportAiSetsForApply.find(aiSet => aiSet.id == id)?.name;
  }

  /** AI自動作成履歴チェック(作成中のまま一定時間経過していた場合にエラー更新) */
  protected checkAiGenerateHistory(type: number, patientId: number) {
    const path =
      type === 1
        ? "/api/patient/vnplan/aigenerate/history/check"
        : "/api/patient/report/aigenerate/history/check";

    this.postJsonBackgroundIgnore(
      window.base_url + path,
      {
        patient_id: patientId
      },
      () => {
        //
      }
    );
  }
}
